type TipoNi = "cpf" | "cnpj";

export class NiUtils {
  /**
   * Gera o NI completo, adicionando os dígitos verificadores necessários.
   * 
   * @param raiz Valor que representa a raiz do NI, podendo ser numérico ou texto.
   * @param tipo Define se o NI é do tipo CPF ou CNPJ (padrão: 'cpf').
   * @returns Uma string contendo o NI completo com os dígitos verificadores.
   */
  public static obterNiCompleto(raiz: number | string, tipo: TipoNi = "cpf"): string {
    raiz = NiUtils.prepararNi(raiz, tipo, false);

    const dv1 = NiUtils.calcularDv(raiz);
    const dv2 = NiUtils.calcularDv(raiz + dv1);

    return raiz + dv1 + dv2;
  }

  public static formatarNi(ni: number | string, tipo: TipoNi = "cpf", isCompleto: boolean = true): string {
    ni = NiUtils.prepararNi(ni, tipo, isCompleto);

    if (tipo === "cpf") {
      const formato = "$1.$2.$3" + (isCompleto ? "-$4" : "");
      return ni.replace(/(\d{3})(\d{3})(\d{3})(\d{2})?/, formato);
    }

    if (tipo === "cnpj") {
      const formato = "$1.$2.$3/$4" + (isCompleto ? "-$5" : "");
      return ni.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})?/, formato);
    }
  }

  /**
   * Gera uma representação intermediária bem definida de NI. O retorno sempre será uma string de dígitos
   * com zeros à esquerda, o tamanho varia de acordo com o tipo de NI (CPF ou CNPJ) e se o mesmo 
   * é completo ou incompleto (possui ou não os dígitos verificadores).
   *
   * @param num Valor que representa o NI a ser processado.
   * @param tipoNi Define se o NI é do tipo CPF ou CNPJ (padrão: 'cpf').
   * @param isCompleto Indica se o NI já traz dígitos verificadores (padrão: 'true').
   * @returns Retorna a string contendo o NI processado de acordo com o tipo escolhido.
   */
  private static prepararNi(ni: number | string, tipo: TipoNi = "cpf", isCompleto: boolean = true): string {
    if (tipo != "cpf" && tipo != "cnpj") {
      throw new Error(`Tipo de NI inválido: ${tipo}. Use "cpf" ou "cnpj".`);
    }

    if (ni == null || ni === "") {
      throw new Error(`O NI não pode ser nulo ou vazio para o tipo ${tipo.toUpperCase()}.`);
    }

    ni = ni.toString().replace(/\D/g, "");

    let tamanhoLimite = tipo === "cpf" ? 9 : 12;

    if (isCompleto) {
      tamanhoLimite += 2;
    }

    if (ni.length > tamanhoLimite) {
      throw new Error(`A raiz do ${tipo.toUpperCase()} deve ter ${tamanhoLimite} dígitos no máximo.`);
    }

    return ni.padStart(tamanhoLimite, '0');
  }

  /**
   * @param base Base do CPF (9 ou 10 dígitos) ou base do CNPJ (12 ou 13 dígitos) => {raiz} ou {raiz + dv1}
   * @returns Retorna o primeiro ou o segundo dígito verificador.
   */
  private static calcularDv(base: string): number {
    const digitos = base.split('').map(Number).reverse();
    const isCnpj = digitos.length >= 12;

    let somatorio = 0;

    for (let i = 0; i < digitos.length; i++) {
      const peso = isCnpj ? (i % 8) + 2 : (i + 2);
      somatorio += digitos[i] * peso;
    }

    const resto = somatorio % 11;
    return resto < 2 ? 0 : 11 - resto;
  }
}